import { createRemoteJWKSet, jwtVerify, decodeJwt } from 'jose';
import urljoin from 'url-join';
export class AuthServiceV3Impl {
    constructor(sharedEventEmitter, authServiceOptions, loginAllCallback, authServiceState, envConfigService, cookieService, logger) {
        this.sharedEventEmitter = sharedEventEmitter;
        this.authServiceOptions = authServiceOptions;
        this.loginAllCallback = loginAllCallback;
        this.authServiceState = authServiceState;
        this.envConfigService = envConfigService;
        this.cookieService = cookieService;
        this.logger = logger;
        this.eventTimeout = 500;
        this.cookieService.registerCookies([
            {
                name: 'SMSESSION',
                classification: 'essential',
            },
            {
                name: 'ID-TOKEN',
                classification: 'essential',
            },
        ]);
        this.jwks = [
            // sandbox
            'https://identity-sandbox.vwgroup.io/v1/jwks',
            // EMEA
            'https://identity.vwgroup.io/v1/jwks',
            // Asia Pacific
            'https://identity.ap.vwgroup.io/v1/jwks',
            // North America
            'https://identity.na.vwgroup.io/v1/jwks',
        ].reduce((all, jwksUrl) => {
            const url = new URL(jwksUrl);
            return Object.assign(Object.assign({}, all), { [url.origin]: createRemoteJWKSet(url, {
                    cacheMaxAge: authServiceOptions.jwksCacheTime,
                }) });
        }, {});
        this.verifiedTokens = {};
    }
    async isAuthenticated() {
        try {
            await this.getValidTokens();
            return true;
        }
        catch (e) {
            return false;
        }
    }
    async login(prompt, redirectUrl) {
        await this.sharedEventEmitter.emit('saveState');
        this.loginAllCallback(prompt, redirectUrl);
    }
    async logout(redirectUrl) {
        await this.sharedEventEmitter.emit('logout');
        const { ssoUrl: logoutUrl } = this.authServiceOptions;
        const logoutRedirectUrl = redirectUrl ||
            (typeof this.authServiceOptions.defaultLogoutRedirectUrl === 'function'
                ? this.authServiceOptions.defaultLogoutRedirectUrl()
                : this.authServiceOptions.defaultLogoutRedirectUrl);
        const url = new URL(logoutUrl, window.location.origin);
        url.searchParams.set('action', 'logout');
        if (logoutRedirectUrl) {
            url.searchParams.set('targetUrl', logoutRedirectUrl);
        }
        window.location.assign(url.toString());
    }
    async getAccessToken() {
        const validTokens = await this.getValidTokens();
        return validTokens.access_token;
    }
    async getIdToken() {
        const validTokens = await this.getValidTokens();
        return validTokens.id_token;
    }
    subscribe(event, handlerFunction) {
        this.sharedEventEmitter.on(event, async () => {
            // eslint-disable-next-line no-promise-executor-return
            const timeoutPromise = new Promise((resolve) => setTimeout(resolve, this.eventTimeout));
            return Promise.race([timeoutPromise, handlerFunction()]);
        });
    }
    async getFormProviderBaseUrl(path, formProvider = 'forms') {
        var _a;
        const config = await this.envConfigService.getConfiguration(formProvider);
        const baseUrl = (_a = config === null || config === void 0 ? void 0 : config.cookieAuthBaseUrls[window.location.host]) !== null && _a !== void 0 ? _a : config === null || config === void 0 ? void 0 : config.cookieAuthBaseUrls.default;
        if (!baseUrl) {
            throw new Error(`${formProvider} base URL not found for current host and default is also not defined!`);
        }
        const url = path ? urljoin(baseUrl, path) : baseUrl;
        // strip trailing /
        return url.replace(/\/$/, '');
    }
    async getFormsBaseUrl(path) {
        return this.getFormProviderBaseUrl(path, 'forms');
    }
    async getFormBaseUrl(path) {
        return this.getFormProviderBaseUrl(path, 'form');
    }
    async getValidTokens() {
        const unauthMessage = 'User is not authenticated';
        if ((this.authServiceState.unauthResponseExpire || 0) >= Date.now()) {
            throw new Error(unauthMessage);
        }
        return this.authServiceState.mutex.use(async () => {
            const hasValidTokens = await this.areTokensValid(this.authServiceState.tokens);
            if (!this.authServiceState.tokens || !hasValidTokens) {
                const tokenResponse = await this.retrieveValidTokens();
                if (tokenResponse) {
                    this.authServiceState.tokens = tokenResponse;
                    return tokenResponse;
                }
                // Don't remember the unauth response. Reading cookies costs "nothing",
                // but caching the response will prevent the other versions from checking themselves
                throw new Error(unauthMessage);
            }
            return this.authServiceState.tokens;
        });
    }
    async retrieveValidTokens() {
        const accessToken = this.cookieService.get('SMSESSION');
        const idToken = this.cookieService.get('ID-TOKEN');
        if (!accessToken || !idToken) {
            return undefined;
        }
        const tokens = {
            access_token: accessToken,
            id_token: idToken,
        };
        if (!(await this.areTokensValid(tokens))) {
            throw new Error('Tokens are not valid');
        }
        return tokens;
    }
    async areTokensValid(tokens) {
        return Boolean(tokens &&
            (await this.isJwtTokenValid(tokens.access_token, 'access token')) &&
            (await this.isJwtTokenValid(tokens.id_token, 'id token')));
    }
    async isJwtTokenValid(jwtToken, tokenType) {
        let decoded;
        try {
            decoded = decodeJwt(jwtToken);
            // Check if this token has already been verified and has not expired. No need to do it again.
            if (this.verifiedTokens[jwtToken] && decoded.exp && decoded.exp * 1000 > Date.now()) {
                return true;
            }
            const { iss: issuer = '' } = decoded;
            const jwks = this.jwks[issuer];
            if (!jwks) {
                throw new Error(`Issuer is invalid: ${issuer}`);
            }
            await jwtVerify(jwtToken, jwks, {
                issuer,
            });
            if (!this.verifiedTokens[jwtToken]) {
                this.verifiedTokens[jwtToken] = true;
            }
            return true;
        }
        catch (e) {
            this.logger.error(`${tokenType} is invalid`, e, decoded || 'invalid format');
            return false;
        }
    }
}
//# sourceMappingURL=AuthServiceV3.js.map